import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaceLandmarker } from '@mediapipe/tasks-vision';
import { useSession } from '../store/session';
import { getFaceLandmarker } from '../lib/face';
import {
  computeFaceBoundsList,
  getObjectFitCoverTransform,
  mapBoundsToElementRect,
  pickFaceIndexByAnchor,
  pickFaceIndexByClick,
  type FaceAnchor,
  type FaceBounds,
} from '../lib/faceSelection';
import { requestFullscreen } from '../utils/requestFullscreen';
import { isIPad } from '../utils/isIPad';
import { unlockAudio } from '../utils/audio';
import { getOrCreateClientRunId } from '../lib/clientRun';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';

export default function Start() {
  const nav = useNavigate();
  const setStream = useSession((s) => s.setStream);
  const setHasPermission = useSession((s) => s.setHasPermission);
  const setFaceOK = useSession((s) => s.setFaceOK);
  const selectedFaceAnchor = useSession((s) => s.selectedFaceAnchor);
  const setSelectedFaceAnchor = useSession((s) => s.setSelectedFaceAnchor);

  const [landmarkerReady, setLandmarkerReady] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [facesCount, setFacesCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Fullscreen state: true for iPad (inline mode) or when document is actually fullscreen.
  const [fsReady, setFsReady] = useState<boolean>(() => isIPad());
  const [needsFsNudge, setNeedsFsNudge] = useState(false);

  const vref = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const landmarkerRef = useRef<FaceLandmarker>();

  const anchorRef = useRef<FaceAnchor | null>(selectedFaceAnchor);
  const faceOkRef = useRef(false);
  const lastBoundsRef = useRef<FaceBounds[]>([]);

  useEffect(() => {
    anchorRef.current = selectedFaceAnchor;
  }, [selectedFaceAnchor]);

  // fresh run should not inherit a previous selection
  useEffect(() => {
    setSelectedFaceAnchor(null);
    setFaceOK(false);
    faceOkRef.current = false;
  }, [setFaceOK, setSelectedFaceAnchor]);

  // Load face model
  useEffect(() => {
    let cancelled = false;
    getFaceLandmarker()
      .then((lm) => {
        if (!cancelled) {
          landmarkerRef.current = lm;
          setLandmarkerReady(true);
        }
      })
      .catch((e) => setError(`人脸模型加载失败：${e}`));
    return () => {
      cancelled = true;
    };
  }, []);

  // Track fullscreen changes (treat iPad as "ready" always)
  useEffect(() => {
    const onFsChange = () => setFsReady(isIPad() || !!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  async function enableCamera() {
    setError(null);
    setFaceDetected(false);
    setFacesCount(0);
    setSelectedFaceAnchor(null);
    anchorRef.current = null;
    setFaceOK(false);
    faceOkRef.current = false;

    try {
      // 1) Ask for webcam permission FIRST
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });

      // 2) Start preview & mark permission
      if (!vref.current) return;
      vref.current.srcObject = stream;
      await vref.current.play().catch(() => {});
      setStream(stream);
      setHasPermission(true);
      setCameraReady(true);

      // 3) Try to go fullscreen next
      const ok = await requestFullscreen();
      setFsReady(isIPad() || ok || !!document.fullscreenElement);
      if (!(ok || document.fullscreenElement || isIPad())) {
        setNeedsFsNudge(true); // require explicit user click to enter fullscreen
      }
      // Auto face check will start via the effect below once fsReady === true.
    } catch (e) {
      setError((e as Error)?.message ?? '无法访问摄像头。');
    }
  }

  async function enterFullscreenNow() {
    const ok = await requestFullscreen();
    const ready = isIPad() || ok || !!document.fullscreenElement;
    setFsReady(ready);
    if (ready) setNeedsFsNudge(false);
  }

  useEffect(() => {
    let cancelled = false;
    let rafId = 0;
    const last = {
      facesCount: -1,
      faceDetected: false,
    };

    const video = vref.current;
    const canvas = overlayRef.current;
    const lm = landmarkerRef.current;
    if (!cameraReady || !fsReady || !landmarkerReady || !video || !canvas || !lm) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const draw = (bounds: FaceBounds[], selectedIndex: number | null) => {
      const elementW = canvas.clientWidth;
      const elementH = canvas.clientHeight;
      const dpr = window.devicePixelRatio || 1;
      const nextW = Math.max(1, Math.round(elementW * dpr));
      const nextH = Math.max(1, Math.round(elementH * dpr));
      if (canvas.width !== nextW || canvas.height !== nextH) {
        canvas.width = nextW;
        canvas.height = nextH;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, elementW, elementH);

      const t = getObjectFitCoverTransform(elementW, elementH, video.videoWidth, video.videoHeight);
      for (let i = 0; i < bounds.length; i++) {
        const rect = mapBoundsToElementRect(bounds[i], t);
        const isSelected = selectedIndex === i;
        ctx.lineWidth = isSelected ? 3 : 2;
        ctx.strokeStyle = isSelected ? 'rgba(34,197,94,0.95)' : 'rgba(255,255,255,0.55)';
        ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);

        if (bounds.length > 1) {
          ctx.fillStyle = isSelected ? 'rgba(34,197,94,0.95)' : 'rgba(255,255,255,0.75)';
          ctx.font =
            '12px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
          ctx.fillText(String(i + 1), rect.x + 4, rect.y + 14);
        }
      }
    };

    const loop = () => {
      if (cancelled) return;

      const now = performance.now();
      let bounds: FaceBounds[] = [];
      if (video.readyState >= 2) {
        try {
          const res = lm.detectForVideo(video, now);
          const faces = res.faceLandmarks ?? [];
          bounds = computeFaceBoundsList(
            faces as unknown as Array<Array<{ x: number; y: number }>>,
          );
        } catch {
          bounds = [];
        }
      }

      lastBoundsRef.current = bounds;

      const count = bounds.length;
      const anchor = anchorRef.current;

      let selectedIndex: number | null = null;
      let canProceed = false;

      if (count === 0) {
        selectedIndex = null;
        canProceed = false;
      } else if (count === 1) {
        selectedIndex = 0;
        canProceed = true;
        if (!anchor) {
          const nextAnchor = { cx: bounds[0].cx, cy: bounds[0].cy };
          anchorRef.current = nextAnchor;
          setSelectedFaceAnchor(nextAnchor);
        }
      } else {
        if (anchor) {
          selectedIndex = pickFaceIndexByAnchor(bounds, anchor);
          canProceed = true;
        } else {
          selectedIndex = null;
          canProceed = false;
        }
      }

      draw(bounds, selectedIndex);

      if (last.facesCount !== count) {
        last.facesCount = count;
        setFacesCount(count);
      }
      if (last.faceDetected !== canProceed) {
        last.faceDetected = canProceed;
        setFaceDetected(canProceed);
      }
      if (faceOkRef.current !== canProceed) {
        faceOkRef.current = canProceed;
        setFaceOK(canProceed);
      }

      rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [cameraReady, fsReady, landmarkerReady, setFaceOK, setSelectedFaceAnchor]);

  const onPreviewClick = (event: MouseEvent<HTMLCanvasElement>) => {
    const bounds = lastBoundsRef.current;
    if (bounds.length < 2) return;
    const video = vref.current;
    const canvas = overlayRef.current;
    if (!video || !canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    const t = getObjectFitCoverTransform(
      rect.width,
      rect.height,
      video.videoWidth,
      video.videoHeight,
    );
    const idx = pickFaceIndexByClick(bounds, clickX, clickY, t);
    if (idx === null) return;

    const anchor = { cx: bounds[idx].cx, cy: bounds[idx].cy };
    anchorRef.current = anchor;
    setSelectedFaceAnchor(anchor);
  };

  async function startTest() {
    getOrCreateClientRunId();
    // Best-effort: unlock audio before we navigate into the stimulus runner.
    // Must run in a user gesture for iOS/Safari.
    unlockAudio();

    // Be robust: ensure we’re fullscreen before proceeding (no-op on iPad).
    if (!fsReady) {
      const ok = await requestFullscreen();
      setFsReady(isIPad() || ok || !!document.fullscreenElement);
      if (!(ok || document.fullscreenElement || isIPad())) {
        setNeedsFsNudge(true);
        return;
      }
    }
    if (!faceDetected) return;
    nav('/runner/gallery');
  }

  const activeStep = !cameraReady ? 0 : !fsReady ? 1 : !faceDetected ? 2 : 3;
  const needsSelection = facesCount > 1 && !selectedFaceAnchor;

  return (
    <div className="stage" aria-label="Runner start">
      {/* tiny preview overlay while we verify face */}
      <video ref={vref} className="runner-preview" playsInline muted />
      <canvas
        ref={overlayRef}
        className={`runner-preview-overlay${needsSelection ? ' clickable' : ''}`}
        onClick={onPreviewClick}
        aria-label="Face selection overlay"
      />

      {/* centered start UI */}
      <div className="runner-overlay">
        <div className="runner-ui">
          <Stack spacing={1} alignItems="center">
            <Typography variant="h5" component="h2" sx={{ textAlign: 'center', fontWeight: 800 }}>
              测试准备
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, textAlign: 'center', mb: 1 }}>
              依次完成摄像头授权、进入全屏与人脸检测后，即可开始约 90 秒的短视频任务。若检测到多张人脸，请点击右上角选择孩子人脸。
            </Typography>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ width: '100%', mb: 0.5 }}>
              <Step>
                <StepLabel>摄像头</StepLabel>
              </Step>
              <Step>
                <StepLabel>全屏</StepLabel>
              </Step>
              <Step>
                <StepLabel>人脸检测</StepLabel>
              </Step>
              <Step>
                <StepLabel>开始</StepLabel>
              </Step>
            </Stepper>

            {/* Step 1: Enable camera */}
            {!cameraReady && (
              <Button
                onClick={enableCamera}
                disabled={!landmarkerReady}
                variant="contained"
                size="large"
              >
                {landmarkerReady ? '启用摄像头' : '正在加载人脸模型…'}
              </Button>
            )}

            {/* Step 2: Enter fullscreen nudge (if automatic attempt failed) */}
            {cameraReady && !fsReady && needsFsNudge && (
              <Button onClick={enterFullscreenNow} variant="contained" size="large" sx={{ mt: 1 }}>
                进入全屏
              </Button>
            )}

            {/* Step 3: Face status */}
            {cameraReady && fsReady && (
              <Stack spacing={1} alignItems="center" sx={{ mt: 1 }}>
                {facesCount === 0 && (
                  <>
                    <CircularProgress size={22} />
                    <Typography variant="body2">正在检测人脸…</Typography>
                  </>
                )}

                {facesCount === 1 && <Alert severity="success">已检测到人脸 ✓</Alert>}

                {facesCount > 1 && needsSelection && (
                  <Alert severity="warning">检测到多张人脸：请点击右上角选择孩子人脸</Alert>
                )}

                {facesCount > 1 && !needsSelection && faceDetected && (
                  <Alert severity="success">已选择孩子人脸 ✓</Alert>
                )}
              </Stack>
            )}

            {/* Step 4: Start button — enabled after faceDetected */}
            <Button
              onClick={startTest}
              variant="contained"
              size="large"
              disabled={!faceDetected}
              sx={{ mt: 1 }}
            >
              开始测试
            </Button>

            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </Stack>
        </div>
      </div>
    </div>
  );
}
